import React, { memo } from 'react'
import { Input, Text, Link, Textarea, Icon } from '@evertel/web/ui'
import Wrapper from './Wrapper'

export interface TextWriteWidgetProps {
    id: string
    htmlId: string
    value: string
    onChange: (id: string, value: string) => void
    title: string
    description: string
    hint: string
    required?: boolean
    multiline?: boolean
    placeholder?: string
    scope?: string
    errors?: string
    contentType?: string
}

const TextWriteWidget: React.FC<TextWriteWidgetProps> = memo(({
    id,
    htmlId,
    value = '',
    onChange,
    title,
    required,
    multiline,
    placeholder,
    contentType = 'text',
    errors,
    scope,
    ...otherProps
}) => {

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`
    const hasErrors = errors && errors.length > 0

    const inputProps = {
        id: inputId,
        value,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange && onChange(id, e.target.value)
        },
        disabled: scope === 'schemaBuilder',
        required: required ? true : undefined,
        placeholder,
        'aria-labelledby': (title || otherProps.hint) ? labelId : undefined,
        'aria-invalid': hasErrors ? true : undefined,
        'aria-errormessage': hasErrors ? errorId : undefined,
        'aria-required': required ? true : undefined
    }

    const renderIcon = () => {
        if (!contentType || contentType === 'text' || multiline) return null

        let iconName
        let label

        switch (contentType) {
            case 'email':
                iconName = 'envelope'
                label = 'Email'
                break
            case 'url':
                iconName = 'link'
                label = 'URL'
                break
            case 'number':
                iconName = 'hashtag'
                label = 'Number'
                break
            case 'tel':
                iconName = 'phone'
                label = 'Phone'
                break
            default:
                return contentType
        }

        return (
            <span className="input-group-text">
                <Icon name={iconName} faProps={{title: label}}/>
            </span>
        )
    }

    return (
        <Wrapper
            title={title}
            required={required}
            errors={errors}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            {!multiline ? (
                <div className="input-group">
                    {renderIcon()}
                    <Input
                        type={contentType}
                        {...inputProps}
                    />
                </div>
            ) : (
                <Textarea
                    {...inputProps}
                />
            )}
        </Wrapper>
    )
})

// ----------------------------------------------------------------------------------------

export interface TextReadWidgetProps {
    id?: string
    htmlId?: string
    value?: string
    title?: string
    text?: string
    multiline?: boolean
    contentType?: string
    truncate?: boolean
}

const TextReadWidget: React.FC<TextReadWidgetProps> = ({
    htmlId,
    value = '',
    title,
    text,
    multiline,
    contentType = 'text',
    truncate,
    ...otherProps
}) => {

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    if (typeof value === 'object') {
        return null
    }

    if (truncate && value.length > 100) {
        value = `${value.slice(0, 100)}...`
    }

    const renderIcon = () => {
        if (!contentType || contentType === 'text' || multiline) return null

        let iconName
        let label

        switch (contentType) {
            case 'email':
                iconName = 'envelope'
                label = 'Email'
                break
            case 'url':
                iconName = 'link'
                label = 'URL'
                break
            case 'number':
                iconName = 'hashtag'
                label = 'Number'
                break
            case 'tel':
                iconName = 'phone'
                label = 'Phone'
                break
            default:
                return contentType
        }

        return (
            <span className="pe-2">
                <Icon name={iconName} faProps={{title: label}}/>
            </span>
        )
    }

    return (
        <Wrapper
            value={value}
            title={title}
            labelId={labelId}
            descriptionId={descriptionId}
            {...otherProps}
        >
            {!value ? (
                <Text id={inputId}>
                    <span className="visually-hidden">No value</span>
                    <span aria-hidden="true">---</span>
                </Text>
            ) : (
                <div>
                    {renderIcon()}
                    {((contentType !== 'url' && contentType !== 'email') || multiline) ? (
                        <Text
                            id={inputId}
                            {...multiline && {style: { whiteSpace: 'pre-line' }}}
                        >
                            {value}
                        </Text>
                    ) : (
                        <a id={inputId} href={(contentType === 'email') ? `mailto:${value}` : value}>
                            {value}
                        </a>
                    )}
                </div>
            )}
        </Wrapper>
    )
}

export {
    TextWriteWidget,
    TextReadWidget
}
